/* FD rewards: Shopify owns discounts; this controller owns the BMSM gift. */
(function () {
  'use strict';

  if (window.fdCartRewardsController) window.fdCartRewardsController.destroy();

  const cfg = window.fdCartTierConfig;
  if (!cfg) return;

  const offerMode = cfg.offerMode === 'evergreen' ? 'evergreen' : 'bmsm';
  const tiers = (cfg.tiers || []).map(t => ({
    amount: Number(t.amount),
    label: String(t.label || ''),
    code: String(t.code || '').trim(),
    virtual: Boolean(t.virtual)
  }));
  const autoCodes = offerMode === 'bmsm' && Boolean(cfg.autoCodes);
  const autoGift = offerMode === 'bmsm' && Boolean(cfg.autoGift);
  const giftVariantId = Number(cfg.giftVariantId || 0);
  const valid = tiers.length > 0 && tiers.every((tier, index) =>
    Number.isFinite(tier.amount) &&
    tier.amount >= 0 &&
    (!index || tier.amount > tiers[index - 1].amount)
  );

  const root = window.Shopify?.routes?.root || '/';
  const tag = '<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M13 2h9v9L11 22 2 13 13 2Zm5 3a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3Z"/></svg>';
  const check = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m4 12 5 5L20 6" fill="none" stroke="currentColor" stroke-width="4"/></svg>';

  let mounted = [];
  let timer;
  let busy = null;
  let pending = false;
  let disposed = false;
  let internalBuild = false;
  let lastError = '';
  let allowSubmit = false;
  let observer;
  const failures = new Set();
  const listeners = [];

  function listen(el, name, fn, opts) {
    el.addEventListener(name, fn, opts);
    listeners.push(() => el.removeEventListener(name, fn, opts));
  }

  function money(cents) {
    return window.theme?.Currency
      ? theme.Currency.formatMoney(cents, theme.settings.moneyFormat)
      : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
  }

  function eligible(cart) {
    return (cart.items || []).reduce((sum, item) => {
      const gift = item.properties?._free_tier || item.properties?._fd_bmsm_gift || item.final_line_price === 0;
      return sum + (gift ? 0 : Number(item.original_line_price || 0));
    }, 0);
  }

  function targetIndex(total, count) {
    if (!count) return -1;
    let index = -1;
    tiers.forEach((tier, i) => {
      if (total >= tier.amount) index = i;
    });
    return index;
  }

  function progress(total, count) {
    if (!count || tiers.length < 2) return 0;
    const index = targetIndex(total, count);
    if (index < 0) return 0;
    if (index >= tiers.length - 1) return 1;
    const segment = (total - tiers[index].amount) / (tiers[index + 1].amount - tiers[index].amount);
    return (index + segment) / (tiers.length - 1);
  }

  function setContainerVisibility(container, visible) {
    container.hidden = !visible;
    container.style.display = visible ? '' : 'none';
  }

  function mount() {
    mounted = [];

    document.querySelectorAll('[data-cart-goal]').forEach(container => {
      const banner = container.classList.contains('cart-goal-banner-container');
      container.classList.add('fd-rewards-mounted');
      container.classList.toggle('fd-rewards-mounted--evergreen', offerMode === 'evergreen');

      if (!cfg.enabled || (banner && !cfg.showBanner)) {
        setContainerVisibility(container, false);
        return;
      }

      setContainerVisibility(container, true);
      container.innerHTML = '<div class="fd-rewards fd-rewards--' + offerMode + '" style="--fd-tier-count:' + tiers.length + ';--fd-tier-edge:' + (50 / tiers.length) + '%"><div class="fd-rewards-message">' + tag + '<span></span></div><div class="fd-rewards-track"><div class="fd-rewards-rail"><span class="fd-rewards-fill"></span></div><ol class="fd-rewards-list"></ol></div><p class="fd-rewards-status" role="status" aria-live="polite"></p></div>';

      const list = container.querySelector('ol');
      tiers.forEach(tier => {
        const li = document.createElement('li');
        if (tier.virtual) li.classList.add('fd-rewards-spacer');
        li.innerHTML = '<span class="fd-rewards-dot">' + tag + '</span><span class="fd-rewards-label"></span>';
        const label = li.querySelector('.fd-rewards-label');
        const parts = tier.label.split(/\s*\+\s*/);
        label.textContent = parts[0] + (parts.length > 1 ? ' +' : '');
        if (parts.length > 1) {
          const sub = document.createElement('span');
          sub.textContent = parts.slice(1).join(' + ');
          label.append(sub);
        }
        list.append(li);
      });

      mounted.push(container);
    });
  }

  function status(text) {
    mounted.forEach(container => {
      const el = container.querySelector('.fd-rewards-status');
      if (el) el.textContent = text;
    });
  }

  function updateFooterDiscountBadge(cart) {
    const discountLabel = document.querySelector('#CartDrawer .fd-total-discounts > div:first-child');
    if (!discountLabel) return;

    let badge = discountLabel.querySelector('.fd-applied-tier-badge');
    if (!badge) {
      badge = document.createElement('span');
      badge.className = 'fd-applied-tier-badge';
      discountLabel.append(badge);
    }

    const appliedCodes = Array.isArray(cart.discount_codes)
      ? cart.discount_codes.filter(discount => discount.applicable).map(discount => normalized(discount.code))
      : [];
    let appliedTier = offerMode === 'bmsm'
      ? tiers.find(tier => tier.code && appliedCodes.includes(normalized(tier.code)))
      : null;

    // Shopify can expose automatic and line-level savings in total_discount
    // without listing them in discount_codes. In that case, use the reached
    // BMSM tier only after Shopify confirms that the cart has real savings.
    if (!appliedTier && offerMode === 'bmsm' && Number(cart.total_discount || 0) > 0) {
      const reachedIndex = targetIndex(eligible(cart), Number(cart.item_count || 0));
      if (reachedIndex >= 0) appliedTier = tiers[reachedIndex];
    }

    if (!appliedTier) {
      badge.textContent = '';
      badge.hidden = true;
      return;
    }

    const percentage = appliedTier.label.match(/\d+%\s*OFF/i);
    badge.textContent = percentage ? percentage[0].toUpperCase() : appliedTier.label;
    badge.hidden = false;
  }

  function render(cart) {
    const itemCount = Number(cart.item_count || 0);
    const total = eligible(cart);
    const index = targetIndex(total, itemCount);
    const next = tiers[index + 1];
    const hideForEmptyEvergreen = offerMode === 'evergreen' && itemCount === 0;

    updateFooterDiscountBadge(cart);

    mounted.forEach(container => {
      const banner = container.classList.contains('cart-goal-banner-container');
      const shouldShow = cfg.enabled && (!banner || cfg.showBanner) && !hideForEmptyEvergreen;
      setContainerVisibility(container, shouldShow);
      if (!shouldShow) return;

      container.querySelector('.fd-rewards-fill').style.width = (Math.max(0, Math.min(1, progress(total, itemCount))) * 100) + '%';
      container.querySelectorAll('li').forEach((li, i) => {
        const unlocked = i <= index;
        li.classList.toggle('is-unlocked', unlocked);
        li.querySelector('.fd-rewards-dot').innerHTML = unlocked ? check : tag;
        li.setAttribute('aria-label', tiers[i].label + (unlocked ? ' — threshold reached' : ' — upcoming'));
      });

      const message = container.querySelector('.fd-rewards-message span');
      message.replaceChildren();

      if (!itemCount) {
        message.textContent = 'Add an item to unlock ' + tiers[0].label;
      } else if (next) {
        message.append('Add ');
        const amount = document.createElement('u');
        amount.textContent = money(Math.max(0, next.amount - total));
        message.append(amount, ' to unlock ' + next.label);
      } else {
        message.textContent = cfg.success || 'You\'ve unlocked all rewards!';
      }
    });

    status(lastError);
  }

  async function request(path, body) {
    const response = await fetch(root + path, {
      credentials: 'same-origin',
      cache: 'no-store',
      ...(body ? {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'application/json'
        },
        body: JSON.stringify(body)
      } : {})
    });
    const result = await response.json();
    if (!response.ok) throw new Error(result.description || result.message || 'Cart request failed');
    return result;
  }

  async function syncBmsmGift(cart) {
    if (!autoGift || !giftVariantId || tiers.length < 3) {
      return { cart: cart, changed: false };
    }

    const threshold = tiers[2].amount;
    const reached = eligible(cart) >= threshold;
    const giftLine = (cart.items || []).find(item =>
      Number(item.variant_id) === giftVariantId && item.properties?._fd_bmsm_gift
    );

    if (reached && !giftLine) {
      await request('cart/add.js', {
        items: [{
          id: giftVariantId,
          quantity: 1,
          properties: { _fd_bmsm_gift: '1' }
        }]
      });
      return { cart: await request('cart.js'), changed: true };
    }

    if (!reached && giftLine) {
      const updated = await request('cart/change.js', {
        id: giftLine.key,
        quantity: 0
      });
      return { cart: updated, changed: true };
    }

    if (reached && giftLine && Number(giftLine.quantity) !== 1) {
      const updated = await request('cart/change.js', {
        id: giftLine.key,
        quantity: 1
      });
      return { cart: updated, changed: true };
    }

    return { cart: cart, changed: false };
  }

  function normalized(code) {
    return String(code).toUpperCase();
  }

  function rebuild() {
    internalBuild = true;
    document.dispatchEvent(new CustomEvent('cart:build'));
    internalBuild = false;
  }

  async function run() {
    if (disposed || !cfg.enabled) return;
    if (!valid) {
      lastError = 'Check that all reward thresholds are entered in increasing order.';
      status(lastError);
      return;
    }

    let cart = await request('cart.js');
    if (cart.currency && cart.currency !== 'USD') {
      lastError = 'This offer is configured for USD. Currency-specific thresholds are not configured.';
      status(lastError);
      return;
    }

    const giftSync = await syncBmsmGift(cart);
    cart = giftSync.cart;
    render(cart);
    if (!autoCodes) {
      if (giftSync.changed) rebuild();
      return;
    }

    if (tiers.some(tier => !tier.code) || new Set(tiers.map(tier => normalized(tier.code))).size !== tiers.length) {
      lastError = 'Enter distinct Shopify discount codes for every Buy More, Save More tier.';
      status(lastError);
      return;
    }
    if (!Array.isArray(cart.discount_codes)) {
      lastError = 'Unable to verify existing cart codes. Automatic switching paused.';
      status(lastError);
      return;
    }

    const index = targetIndex(eligible(cart), cart.item_count);
    const desired = index >= 0 ? tiers[index].code : '';
    const managed = new Set(tiers.map(tier => normalized(tier.code)));
    const current = cart.discount_codes.filter(discount => managed.has(normalized(discount.code)));
    const external = cart.discount_codes.filter(discount => !managed.has(normalized(discount.code))).map(discount => discount.code);
    const externalApplied = cart.discount_codes.filter(discount => !managed.has(normalized(discount.code)) && discount.applicable).map(discount => discount.code);

    if ((!desired && current.length === 0) || (current.length === 1 && normalized(current[0].code) === normalized(desired) && current[0].applicable)) {
      lastError = '';
      render(cart);
      if (giftSync.changed) rebuild();
      return;
    }

    const key = JSON.stringify([desired, cart.items.map(item => [item.variant_id, item.quantity, item.original_line_price]), external]);
    if (failures.has(key)) {
      lastError = 'Tier discount could not be confirmed. Check code eligibility and combinations.';
      status(lastError);
      return;
    }

    const codes = [...external, ...(desired ? [desired] : [])];
    cart = await request('cart/update.js', { discount: codes.join(',') });
    const acceptedByCode = !desired || (cart.discount_codes || []).some(discount => normalized(discount.code) === normalized(desired) && discount.applicable);
    const acceptedBySavings = Boolean(desired) && Number(cart.total_discount || 0) > 0;
    const accepted = acceptedByCode || acceptedBySavings;
    const lostOther = externalApplied.some(code => !(cart.discount_codes || []).some(discount => normalized(discount.code) === normalized(code) && discount.applicable));

    if (!accepted || lostOther) {
      failures.add(key);
      lastError = lostOther
        ? 'A discount combination was rejected. Check compatibility with KITE.'
        : 'Tier code was not accepted. Check its Shopify settings.';
    } else {
      lastError = '';
    }

    render(cart);
    rebuild();
  }

  function sync() {
    if (busy) {
      pending = true;
      return busy;
    }

    busy = (async () => {
      let rounds = 0;
      do {
        pending = false;
        try {
          await run();
        } catch (error) {
          lastError = 'Unable to update rewards. Please refresh and try again.';
          status(lastError);
          console.warn('FD rewards:', error.message);
        }
      } while (pending && !disposed && ++rounds < 3);
    })().finally(() => {
      busy = null;
    });

    return busy;
  }

  function schedule() {
    if (disposed || internalBuild) return;
    clearTimeout(timer);
    timer = setTimeout(sync, 120);
  }

  async function changeDrawerQuantity(input, quantity) {
    const wrapper = input.closest('.js-qty__wrapper');
    const key = input.dataset.id;
    if (!key || wrapper?.classList.contains('is-loading')) return;

    const nextQuantity = Math.max(0, Number.parseInt(quantity, 10) || 0);
    input.value = nextQuantity;
    wrapper?.classList.add('is-loading');

    try {
      // Do not mutate quantity while a tier-code request is still updating
      // the same Shopify cart. Serializing these requests prevents conflicts.
      clearTimeout(timer);
      if (busy) await busy;

      let cart;
      try {
        if (window.theme?.cart?.changeItem) {
          const nativeResult = await theme.cart.changeItem(key, nextQuantity);
          cart = typeof nativeResult === 'string' ? JSON.parse(nativeResult) : nativeResult;
          if (!cart || cart.status) throw new Error(cart?.message || 'Native cart update failed');
        } else {
          cart = await request('cart/change.js', {
            id: key,
            quantity: nextQuantity
          });
        }
      } catch (firstError) {
        // Some themes/apps intercept change.js. update.js is Shopify's
        // equivalent key-based fallback for the same quantity mutation.
        const updates = {};
        updates[key] = nextQuantity;
        cart = await request('cart/update.js', { updates: updates });
      }

      lastError = '';

      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: { cart: cart }
      }));
      rebuild();
    } catch (error) {
      console.warn('FD cart quantity:', error.message);
      // Rebuild from Shopify so an optimistic number never remains onscreen.
      // Quantity failures do not belong in the reward-status area.
      rebuild();
    } finally {
      wrapper?.classList.remove('is-loading');
    }
  }

  function drawerQuantityClick(event) {
    const button = event.target.closest('#CartDrawer .js-qty__adjust');
    if (!button) return;

    const wrapper = button.closest('.js-qty__wrapper');
    const input = wrapper?.querySelector('.js-qty__num');
    if (!input) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const current = Number.parseInt(input.value, 10) || 0;
    const next = button.classList.contains('js-qty__adjust--plus')
      ? current + 1
      : current - 1;
    changeDrawerQuantity(input, next);
  }

  function drawerQuantityInput(event) {
    const input = event.target.closest('#CartDrawer .js-qty__num');
    if (!input) return;

    event.stopImmediatePropagation();
    changeDrawerQuantity(input, input.value);
  }

  async function checkout(event) {
    if (!autoCodes || !cfg.enabled || allowSubmit) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    clearTimeout(timer);
    const form = event.target;
    const submitter = event.submitter;
    await sync();
    if (disposed) return;
    if (lastError) {
      window.alert(lastError);
      return;
    }
    allowSubmit = true;
    try {
      form.requestSubmit(submitter || undefined);
    } finally {
      allowSubmit = false;
    }
  }

  function init() {
    mount();
    listen(document, 'click', drawerQuantityClick, true);
    listen(document, 'change', drawerQuantityInput, true);
    if (!cfg.enabled) return;

    ['cart:updated', 'cart:build', 'ajaxProduct:added'].forEach(name => listen(document, name, schedule));
    const drawer = document.getElementById('CartDrawer');
    if (drawer) {
      observer = new MutationObserver(schedule);
      observer.observe(drawer, { attributes: true, attributeFilter: ['class'] });
    }

    const products = drawer?.querySelector('[data-products]');
    if (products) {
      const productsObserver = new MutationObserver(schedule);
      productsObserver.observe(products, { childList: true });
      listeners.push(() => productsObserver.disconnect());
    }

    document.querySelectorAll('#CartDrawerForm, form[data-location="cart-page"]').forEach(form => listen(form, 'submit', checkout, true));
    sync();
  }

  window.fdCartRewardsController = {
    destroy() {
      disposed = true;
      clearTimeout(timer);
      observer?.disconnect();
      listeners.forEach(fn => fn());
    }
  };

  if (document.readyState === 'loading') listen(document, 'DOMContentLoaded', init, { once: true });
  else init();
})();